/**
 * Returns the initials from a name, taking the first letter of each word.
 * @param name - The full name to extract initials from
 * @param maxLength - Maximum number of characters to return (default: 2)
 * @returns The initials in uppercase, limited to maxLength characters
 *
 * @example
 * getInitials("John Doe") // Returns "JD"
 * getInitials("Jane Mary Doe", 3) // Returns "JMD"
 * getInitials("John") // Returns "J"
 */
export function getInitials(name: string, maxLength = 2): string {
  if (!name) return "";

  return name
    .split(" ")
    .map((part) => part[0])
    .join("")
    .toUpperCase()
    .substring(0, maxLength);
}

/**
 * Converts text to proper title case following standard English rules.
 * Articles, prepositions, and conjunctions remain lowercase unless they are
 * the first or last word in the string.
 *
 * @param text - The text to convert to title case
 * @returns The text formatted in proper title case
 *
 * @example
 * toTitleCase("CONFLICT OF INTEREST") // Returns "Conflict of Interest"
 * toTitleCase("any other attendees") // Returns "Any Other Attendees"
 * toTitleCase("MEDICAL HISTORY AND BACKGROUND") // Returns "Medical History and Background"
 */
export function toTitleCase(text: string): string {
  if (!text) return "";

  // Words that should remain lowercase in title case (except when first or last word)
  const lowercaseWords = new Set([
    "a",
    "an",
    "and",
    "as",
    "at",
    "but",
    "by",
    "for",
    "if",
    "in",
    "nor",
    "of",
    "on",
    "or",
    "so",
    "the",
    "to",
    "up",
    "yet",
    "with",
    "from",
  ]);

  const words = text.toLowerCase().split(" ");

  return words
    .map((word, index) => {
      // Always capitalize first and last word
      if (index === 0 || index === words.length - 1) {
        return word.charAt(0).toUpperCase() + word.slice(1);
      }

      // Keep articles, prepositions, and conjunctions lowercase
      if (lowercaseWords.has(word)) {
        return word;
      }

      // Capitalize all other words
      return word.charAt(0).toUpperCase() + word.slice(1);
    })
    .join(" ");
}

/**
 * Generates a 6-digit numerical ID from a UUID using a hash algorithm.
 * Useful for creating human-readable IDs from UUIDs.
 *
 * @param uuid - The UUID to convert to a numerical ID
 * @returns A 6-digit numerical string (padded with zeros if needed)
 *
 * @example
 * generateNumericalId("550e8400-e29b-41d4-a716-446655440000") // Returns "123456"
 */
export function generateNumericalId(uuid: string): string {
  let hash = 0;
  for (let i = 0; i < uuid.length; i++) {
    const char = uuid.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash; // Convert to 32-bit integer
  }
  return Math.abs(hash).toString().slice(0, 6).padStart(6, "0");
}
